import { Check, VisibilityOutlined } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ActionFile from "assets/img/svg/actionFile";
import DAS2Icon from "assets/img/svg/dasIcon";
import Replay from "assets/img/svg/replay";
import UrssafIcon from "assets/img/svg/urssafIcon";
import type { ActionCardAction } from "components/shared/ActionCard";
import type { StyledButtonType } from "components/shared/StyledButton";
import type { DeclarationStatus, TimeStatus } from "components/URSSAFDeclarationPage";
import { formatLocaleDateString } from "helpers/utils";
import { t } from "i18next";
import type { IDeclaration } from "store/contributionStore";

import variables from "../../assets/styles/_variables.module.scss";

/**
 * Used to render description on declarations card.
 */
const formatCardDescription = ({
    declarationStatus,
    timeStatus,
    data,
    type,
    isStandalone,
}: {
    declarationStatus: DeclarationStatus;
    timeStatus: TimeStatus;
    data: {
        startDate: Date;
        endDate: Date;
        declaredAt: string | Date | null;
        downloadedAt: string | Date | null;
    };
    type: "urssaf" | "das2";
    isStandalone: boolean;
}) => {
    if (timeStatus === "current" && ["neutral", "error"].includes(declarationStatus)) {
        // If declaration hasn't been downloaded yet.
        if (!data.downloadedAt) {
            if (type === "urssaf") {
                return t("Declare.DeclareURSSAFDateDescription", {
                    startDate: formatLocaleDateString({ date: data.startDate }),
                    endDate: formatLocaleDateString({ date: data.endDate }),
                });
            } else {
                return t("Declarations.DAS2.DeclareDAS2DateDescription", {
                    startDate: formatLocaleDateString({ date: data.startDate }),
                    endDate: formatLocaleDateString({ date: data.endDate }),
                });
            }
            // If declaration hasn't been set as declared yet.
        } else if (!data.declaredAt) {
            return (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        // color: variables["success"],
                    }}
                >
                    <CheckCircleOutlineIcon sx={{ width: "16px" }} />
                    {
                        t("Declarations.DAS2.FileDownloaded", {
                            date: formatLocaleDateString({
                                date: data.downloadedAt ?? "",
                                options: { numeric: true, timeZone: "UTC" },
                            }),
                        }) as string
                    }
                </div>
            );
        }
    } else if (timeStatus === "future") {
        return t("Declare.DeclareURSSAFFutureDescription", {
            date: formatLocaleDateString({ date: data.startDate }),
        });
    } else if (timeStatus === "history") {
        // If function called from URSSAF module:
        if (isStandalone) {
            // Not sure here which value will be kept for old accounts, if declaredAt or downloadedAt.
            return t("Declare.DeclareURSSAFHistoryDescription", {
                date: formatLocaleDateString({ date: new Date(data.declaredAt ?? data.downloadedAt ?? "") }),
            });
        } else {
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <CheckCircleOutlineIcon sx={{ width: "16px" }} />
                    {
                        t("Declarations.DAS2.FileDeclared", {
                            date: formatLocaleDateString({
                                date: data.declaredAt ?? "",
                                options: { numeric: true, timeZone: "UTC" },
                            }),
                        }) as string
                    }
                </div>
            );
        }
    }
};

/**
 * Used to format action buttons on declaration card.
 */
const formatCardAction = ({
    timeStatus,
    isStandalone,
    data,
    previewAction,
    declareAction,
    saveAction,
    declarationStatus,
}: {
    timeStatus: TimeStatus;
    isStandalone: boolean;
    data: IDeclaration;
    declarationStatus: DeclarationStatus;
    previewAction: (contribution: IDeclaration) => void;
    declareAction: (contribution: IDeclaration) => void;
    saveAction?: (contribution: IDeclaration) => void;
}): ActionCardAction | ActionCardAction[] | undefined => {
    if (timeStatus === "current") {
        if (!data.downloadedAt) {
            return {
                icon: <ActionFile />,
                label: isStandalone
                    ? t("Declare.DeclareURSSAF")
                    : data.type === "urssaf"
                      ? t("Declarations.PreviewDownload")
                      : t("Declarations.DAS2.DownloadFile"),
                onClick: () => declareAction(data),
                tooltip: "",
            };
        } else if (!data.declaredAt) {
            return [
                {
                    icon: <Check />,
                    label: t("Declarations.SetAsDeclared"),
                    onClick: () => saveAction && saveAction(data),
                    buttonType: "outlined" as StyledButtonType,
                    tooltip: t("Declarations.UploadedTooltip"),
                    sx: {
                        backgroundColor: `${declarationStatus === "error" ? "#FCEFF3" : "#FAFAFA"} !important`,
                        paddingLeft: "4px !important",
                    },
                },
                {
                    icon: <Replay />,
                    label: "",
                    onClick: () => declareAction(data),
                    buttonType: "outlined" as StyledButtonType,
                    tooltip: t("Declarations.ReplayTooltip"),
                    sx: {
                        backgroundColor: `${declarationStatus === "error" ? "#FCEFF3" : "#FAFAFA"} !important`,
                    },
                },
            ];
        }
    } else if (timeStatus === "history") {
        return [
            {
                icon: <VisibilityOutlined />,
                label: "",
                buttonType: "secondary" as StyledButtonType,
                tooltip: t("Declarations.PreviewTooltip"),
                onClick: () => previewAction(data),
            },
            {
                icon: <Replay />,
                label: "",
                buttonType: "secondary" as StyledButtonType,
                tooltip: t("Declarations.ReplayTooltip"),
                onClick: () => declareAction(data),
            },
        ];
    } else {
        return undefined;
    }
};

/**
 * Returns array with data to display cards for URSSAF declarations.
 * @param contributionList List of contributions.
 * @param previewAction Actions for the preview button.
 * @param declareAction Actions for the declare button.
 */
export const getDeclarationCardsData = ({
    contributionList,
    previewAction,
    declareAction,
    saveAction,
    isStandalone = false,
}: {
    contributionList: IDeclaration[];
    previewAction: (contribution: IDeclaration) => Promise<void>;
    declareAction: (contribution: IDeclaration) => Promise<void>;
    saveAction?: (contribution: IDeclaration) => Promise<void>;
    /**
     * Is called from standalone module.
     */
    isStandalone?: boolean;
}) => {
    if (contributionList?.length > 0) {
        return contributionList
            .map((contrib, item) => {
                const type = contrib.type;
                let declarationStatus: DeclarationStatus = "neutral";
                let timeStatus: TimeStatus = "current";
                const now = new Date();
                const startDate = new Date(contrib.startDate);
                startDate.setDate(startDate.getDate() - 7);
                const endDate = new Date(contrib.endDate);
                endDate.setDate(endDate.getDate() - 7);

                // if now is not between startDate and endDate and declaredAt is not null, then declaration is done
                if (now < startDate && now > endDate && contrib.declaredAt != null) declarationStatus = "success";
                // if now is between startDate and endDate and declaredAt is null, then declaration is to do
                if (now > startDate && now < endDate && contrib.declaredAt == null) declarationStatus = "neutral";
                // if now is before endDate and declaredAt is null, then declaration is urgent to do
                if (now > endDate && contrib.declaredAt == null) declarationStatus = "error";

                if (now < startDate) timeStatus = "future";
                if (contrib.declaredAt !== null) timeStatus = "history";

                // reset date to show them
                startDate.setDate(startDate.getDate() + 7);
                endDate.setDate(endDate.getDate() + 7);

                return {
                    id: contrib.id,
                    icon: type == "urssaf" ? <UrssafIcon /> : <DAS2Icon />,
                    title:
                        type === "urssaf"
                            ? t("Declare.DeclareURSSAFDateTitle", {
                                  count: contrib.quarter,
                                  year: contrib.year,
                              })
                            : t("Declarations.DAS2.DAS2Year", { year: contrib.year }),
                    description: formatCardDescription({
                        declarationStatus,
                        timeStatus,
                        data: {
                            startDate,
                            endDate,
                            downloadedAt: contrib.downloadedAt,
                            declaredAt: contrib.declaredAt,
                        },
                        type: contrib.type,
                        isStandalone,
                    }),
                    style: {
                        backgroundColor:
                            declarationStatus == "success"
                                ? "#ECFAED"
                                : declarationStatus == "error"
                                  ? "#FCEFF3"
                                  : "#FAFAFA",
                    },
                    sortingDate: new Date(contrib.startDate),
                    action: formatCardAction({
                        timeStatus,
                        declarationStatus,
                        isStandalone,
                        data: contrib,
                        previewAction,
                        declareAction,
                        saveAction,
                    }),
                    timeStatus,
                    declarationStatus,
                    quarter: contrib.quarter,
                    year: contrib.year,
                    endDate: contrib.endDate,
                    downloadedAt: contrib.downloadedAt,
                    declaredAt: contrib.declaredAt,
                    type: contrib.type,
                };
            })
            .sort((a: any, b: any) => +a.sortingDate - +b.sortingDate);
    } else {
        return [];
    }
};
